import React, { useState, useEffect } from 'react';
import axios from 'axios';
import LoginPage from './screens/loginPage';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import PatientDashboard from './screens/PatientDashboard';
import NursesPage from './screens/NursesPage';
import OperationsDashboard from './screens/OperationsDashboard';
import AssignmentDashboard from './screens/AssignmentDashboard';
import SettingsPage from './screens/SettingsPage';
import Casesheet from './screens/CaseSheet';
import CaseSheetIndex from './screens/CaseSheetIndex';
import SalarySheetIndex from './screens/SalarySheetIndex';
import SalarySheet from './screens/SalarySheet';
import ManagementDailySalarySheet from './screens/ManagementDailySalarySheet';
import IDCardDashboard from './screens/IDCardDashboard';
import ExperienceDashboard from './screens/ExperienceDashboard';
import AdministrationLayout from './screens/AdministrationLayout';
import ManagementStaffTab from './screens/ManagementStaffTab';
import PhsyioTab from './screens/PhsyioTab';
import MedicalDevicesTab from './components/MedicalDevicesTab';
import ConsumablesDashboard from './screens/Consumables/ConsumablesDashboard';
import BillingIndex from './screens/BillingIndex';
import CurrentClientList from './screens/CurrentClientList';
import CurrentPaymentCollections from './screens/CurrentPaymentCollections';
import PatientBillingHistory from './screens/PatientBillingHistory';
import BillingSheet from './screens/BillingSheet';
import DeletedBills from './screens/DeletedBills';
import CreatePaymentReceipt from './screens/CreatePaymentReceipt';
import PaymentCollectionPage from './screens/PaymentCollectionPage';
import FinancialModule from './screens/FinancialModule';
import FinancialDashboard from './screens/FinancialDashboard';
import SpendAnalyser from './screens/SpendAnalyser';
import LedgerWorkspace from './screens/LedgerWorkspace';
import PettyCash from './screens/PettyCash';
import CreateConsumableBill from './screens/CreateConsumableBill';
import EditConsumableBill from './screens/EditConsumableBill';
import CreateConsumableReturn from './screens/CreateConsumableReturn';
import EditConsumableReturn from './screens/EditConsumableReturn';
import { useReferenceStore, ReferenceStore } from './store/useReferenceStore';
import { installDateYearGuard } from './utils/dateYearGuard';
import ErrorBoundary from './components/ErrorBoundary';
import { ColumnLayoutsProvider } from './components/ColumnLayoutsProvider';
import { Toaster } from 'react-hot-toast';

const SessionExpiredModal: React.FC<{ onLoginAgain: () => void }> = ({ onLoginAgain }) => (
  <div style={{
    position: 'fixed', inset: 0, zIndex: 99999,
    backgroundColor: 'rgba(15,23,42,0.75)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  }}>
    <div style={{
      backgroundColor: 'white', borderRadius: '16px', padding: '2.5rem 2rem',
      width: '380px', maxWidth: '90vw', textAlign: 'center',
      boxShadow: '0 24px 64px rgba(0,0,0,0.3)',
    }}>
      <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>🔒</div>
      <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.15rem', fontWeight: 700, color: '#0f172a' }}>
        Session Expired
      </h3>
      <p style={{ margin: '0 0 1.75rem', fontSize: '0.9rem', color: '#64748b', lineHeight: 1.6 }}>
        Your login session has expired. Please log in again to continue.
      </p>
      <button
        onClick={onLoginAgain}
        style={{
          width: '100%', padding: '0.7rem', backgroundColor: '#2563eb',
          color: 'white', border: 'none', borderRadius: '10px',
          cursor: 'pointer', fontSize: '0.95rem', fontWeight: 600,
        }}
      >
        Log In Again
      </button>
    </div>
  </div>
);

const App: React.FC = () => {
  const [token, setToken] = useState<string | null>(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  const fetchConfig = useReferenceStore((state: ReferenceStore) => state.fetchConfig);

  useEffect(() => {
    const storedToken = localStorage.getItem('accessToken');
    if (storedToken) setToken(storedToken);
  }, []);

  // Global 401 interceptor — fires for any API call across the whole app
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && localStorage.getItem('accessToken')) {
          setSessionExpired(true);
        }
        return Promise.reject(error);
      },
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  useEffect(() => {
    const handleGlobalWheel = (e: WheelEvent) => {
      if (document.activeElement instanceof HTMLInputElement && document.activeElement.type === 'number') {
        document.activeElement.blur();
      }
    };
    window.addEventListener('wheel', handleGlobalWheel);
    return () => window.removeEventListener('wheel', handleGlobalWheel);
  }, []);

  // Global date-year guard — <input type="date"> stores "0026" (not 2026) when a
  // user types just "26" into the year field. Installs one document-wide listener
  // that, on focusout (never mid-typing), rewrites any 2-digit year to 20xx across
  // every screen. Logic + tests live in utils/dateYearGuard.
  useEffect(() => installDateYearGuard(), []);

  useEffect(() => {
    if (token) fetchConfig();
  }, [token, fetchConfig]);

  const handleLoginSuccess = (newToken: string) => {
    setToken(newToken);
    localStorage.setItem('accessToken', newToken);
  };

  const handleLogout = () => {
    setToken(null);
    localStorage.removeItem('accessToken');
  };

  const handleSessionExpired = () => {
    setSessionExpired(false);
    handleLogout();
  };

  return (
    <BrowserRouter>
      <ColumnLayoutsProvider token={token}>
      <Toaster position="top-right" reverseOrder={false} />
      {sessionExpired && <SessionExpiredModal onLoginAgain={handleSessionExpired} />}
      <ErrorBoundary>
      <Routes>
        {!token ? (
          <Route path="/*" element={<LoginPage onLoginSuccess={handleLoginSuccess} />} />
        ) : (
          <Route path="/*" element={<Layout onLogout={handleLogout} />}>
            <Route path="nurses" element={<NursesPage />} />
            <Route path="idcards" element={<IDCardDashboard />} />
            <Route path="experience-certificates" element={<ExperienceDashboard />} />
            <Route path="patients" element={<PatientDashboard />} />
            <Route path="operations" element={<OperationsDashboard />} />
            <Route path="assignments" element={<AssignmentDashboard />} />
            <Route path="casesheet" element={<CaseSheetIndex />} />
            <Route path="casesheet/:id" element={<Casesheet />} />
            <Route path="salarysheet" element={<SalarySheetIndex />} />
            <Route path="salarysheet/management/:id" element={<ManagementDailySalarySheet />} />
            <Route path="salarysheet/:type/:id" element={<SalarySheet />} />
            <Route path="administration" element={<AdministrationLayout />}>
              <Route index element={<Navigate to="management-staff" replace />} />
              <Route path="management-staff" element={<ManagementStaffTab />} />
              <Route path="physio"           element={<PhsyioTab />} />
              <Route path="medical-devices"  element={<MedicalDevicesTab />} />
              <Route path="consumables"      element={<ConsumablesDashboard />} />
            </Route>
            <Route path="billing" element={<BillingIndex />} />
            <Route path="billing/current-clients" element={<CurrentClientList />} />
            <Route path="billing/patient/:patientId" element={<PatientBillingHistory />} />
            <Route path="billing/new/:patientId" element={<BillingSheet />} />
            <Route path="billing/equipment-rental/new/:patientId" element={<BillingSheet />} />
            <Route path="billing/equipment-sale/new/:patientId" element={<BillingSheet />} />
            <Route path="billing/bill/:billId" element={<BillingSheet />} />
            <Route path="billing/deleted" element={<DeletedBills />} />
            <Route path="billing/receipt/:billId" element={<CreatePaymentReceipt />} />
            <Route path="billing/collections" element={<PaymentCollectionPage />} />
            <Route path="billing/current-collections" element={<CurrentPaymentCollections />} />
            <Route path="financial" element={<FinancialModule />} />
            <Route path="financial/dashboard" element={<FinancialDashboard />} />
            <Route path="financial/spend-analyser" element={<SpendAnalyser />} />
            <Route path="financial/ledger" element={<LedgerWorkspace />} />
            <Route path="financial/petty-cash" element={<PettyCash />} />
            <Route path="billing/consumables/new/:patientId" element={<CreateConsumableBill />} />
            <Route path="billing/consumables/edit/:billId" element={<EditConsumableBill />} />
            <Route path="billing/consumables/return/new/:patientId" element={<CreateConsumableReturn />} />
            <Route path="billing/consumables/return/edit/:billId" element={<EditConsumableReturn />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route index element={<Navigate to="/patients" />} />
          </Route>
        )}
      </Routes>
      </ErrorBoundary>
      </ColumnLayoutsProvider>
    </BrowserRouter>
  );
};

export default App;
